// 研究页：问题、过程（拆成几路、各找到多少、补查了什么）、报告（引用编号点开看原文）、依据（按是谁的分组）。
// 跑着的时候每秒取一次进度，写报告时能看到写到哪儿了。

import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { App as AntApp, Popover } from 'antd'
import { AlertCircle, Check, ChevronDown, Copy, FileText, Lightbulb, RotateCw } from 'lucide-react'
import { getResearch, KIND_LABEL, RESEARCH_RUNNING, retryResearch, type ResearchSession, type ResearchSource, type SourceKind } from '../../api/research'
import { IconButton, Pill } from '../../ui'
import { Orb, useElapsed } from '../../ui/Thinking'
import { useDocPreview } from '../kb/usePreview'
import { cleanTitle } from '../kb/DocCard'
import '../chat/chat.css'
import './research.css'

const running = (s?: ResearchSession) => !!s && RESEARCH_RUNNING.includes(s.phase)
const md = (date?: string) => (date ? `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}` : '')
const tagOf = (s: ResearchSource) => (s.kind === 'daily' && s.date ? `日报 ${md(s.date)}` : KIND_LABEL[s.kind])

/** 报告里的 [3]、[2][5] 换成链接，渲染时变成编号角标。 */
const linkCites = (text: string, count: number) => text.replace(/\[(\d{1,2})\](?!\()/g, (m, n) => (Number(n) >= 1 && Number(n) <= count ? `[${n}](#cite-${n})` : m))

function SourcePop({ source, children }: { source: ResearchSource; children: ReactNode }) {
  const { open } = useDocPreview()
  const content = (
    <div className="cite-pop">
      <b>{cleanTitle(source.title)}</b>
      <span className="research-pop-tag">{tagOf(source)}</span>
      {source.passages[0] && <p>{source.passages[0].slice(0, 220)}{source.passages[0].length > 220 ? '…' : ''}</p>}
      <div>
        <Pill size="sm" icon={<FileText />} onClick={() => open(source.knowledgeId, source.kbId)}>
          打开这篇资料
        </Pill>
      </div>
    </div>
  )
  return (
    <Popover content={content} trigger={['click', 'hover']} mouseEnterDelay={0.25} placement="top">
      {children}
    </Popover>
  )
}

export function ResearchPage() {
  const { researchId = '' } = useParams()
  const q = useQuery({ queryKey: ['research', researchId], queryFn: () => getResearch(researchId), refetchInterval: (x) => (running(x.state.data) ? 1000 : false) })
  const s = q.data

  return (
    <div className="chat research">
      <header className="chat-top">
        <h1>{s ? '深入研究' : ''}</h1>
      </header>
      <div className="thread" aria-label="研究">
        {q.isError && <p className="empty-chat">没能载入这次研究：{q.error.message}</p>}
        {s && (
          <>
            <div className="turn-user">{s.question}</div>
            <Report s={s} />
          </>
        )}
      </div>
    </div>
  )
}

function Report({ s }: { s: ResearchSession }) {
  const { message } = AntApp.useApp()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const live = running(s)
  const retry = useMutation({
    mutationFn: () => retryResearch(s.id),
    onSuccess: (r) => qc.setQueryData(['research', s.id], r),
    onError: (e) => void message.error(e.message),
  })
  const markdown = useMemo(() => linkCites(s.report, s.sources.length), [s.report, s.sources.length])
  const components: Components = useMemo(
    () => ({
      a: ({ href, children }) => {
        const n = href?.match(/^#cite-(\d+)$/)?.[1]
        const source = n ? s.sources.find((x) => x.n === Number(n)) : undefined
        if (source)
          return (
            <SourcePop source={source}>
              <button type="button" className="sk-cite" aria-label={`引用 ${source.n}：${cleanTitle(source.title)}`}>
                {source.n}
              </button>
            </SourcePop>
          )
        return <>{children}</>
      },
      img: () => null,
    }),
    [s.sources],
  )

  const copy = async () => {
    const cited = citedIn(s.report)
    const list = s.sources.filter((x) => cited.has(x.n)).map((x) => `[${x.n}] ${cleanTitle(x.title)}（${tagOf(x)}）`).join('\n')
    try {
      await navigator.clipboard.writeText(`${s.report.trim()}\n\n依据：\n${list}`)
      message.success('已复制')
    } catch {
      message.error('没能复制，浏览器不让读写剪贴板')
    }
  }
  // 拿去思考讨论：问题当议题，用得最多的几篇当指定资料（思考室最多 6 篇）
  const think = () => navigate('/', { state: { mode: 'think', topic: s.question, contextKnowledgeIds: [...s.sources].sort((a, b) => b.hits - a.hits).slice(0, 6).map((x) => x.knowledgeId) } })

  return (
    <article className="answer research-report" aria-busy={live} aria-live={live ? 'polite' : undefined}>
      <Progress s={s} />
      {markdown && (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} skipHtml>
          {markdown}
        </ReactMarkdown>
      )}
      {s.phase === 'failed' && (
        <p className="answer-error" role="alert">
          <AlertCircle width={16} height={16} />
          {s.error ?? '研究没做完'}
          <Pill size="sm" icon={<RotateCw />} onClick={() => retry.mutate()} disabled={retry.isPending}>
            重试
          </Pill>
        </p>
      )}
      {s.phase === 'done' && s.report && (
        <div className="answer-acts">
          <IconButton label="复制报告" onClick={copy}>
            <Copy />
          </IconButton>
          {s.sources.length > 0 && (
            <Pill size="sm" variant="ghost" icon={<Lightbulb />} onClick={think} title="把这个问题和用得最多的几篇资料带去思考讨论，最后由你下判断">
              拿去思考讨论
            </Pill>
          )}
        </div>
      )}
      {s.sources.length > 0 && !live && <Sources sources={s.sources} report={s.report} />}
    </article>
  )
}

const ORDER: SourceKind[] = ['mine', 'think', 'project', 'collected', 'daily']

/** 报告里用到了哪几个编号。 */
export const citedIn = (report: string) => new Set([...report.matchAll(/\[(\d{1,2})\]/g)].map((m) => Number(m[1])))

function SourceRow({ x }: { x: ResearchSource }) {
  const { open } = useDocPreview()
  return (
    <button type="button" className="ref" onClick={() => open(x.knowledgeId, x.kbId)}>
      <span className="sk-cite">{x.n}</span>
      <span className="t">{cleanTitle(x.title)}</span>
      {x.kind === 'daily' && x.date && !x.title.includes(x.date) && <span className="ref-tag">{md(x.date)}</span>}
      <FileText width={14} height={14} />
    </button>
  )
}

/** 依据：报告里引用了的，按是谁的分组（我写的在最前）；查到了、报告里没用上的收在最后。 */
function Sources({ sources, report }: { sources: ResearchSource[]; report: string }) {
  const cited = citedIn(report)
  const used = sources.filter((x) => cited.has(x.n))
  const unused = sources.filter((x) => !cited.has(x.n))
  const groups = ORDER.map((kind) => ({ kind, items: used.filter((x) => x.kind === kind).sort((a, b) => a.n - b.n) })).filter((g) => g.items.length)
  return (
    <section className="basis research-basis" aria-label="依据">
      <h2>依据 · {used.length} 篇</h2>
      {groups.map((g) => (
        <div key={g.kind} className="research-group" role="group" aria-label={KIND_LABEL[g.kind]}>
          <h3>{KIND_LABEL[g.kind]}</h3>
          {g.items.map((x) => (
            <SourceRow key={x.n} x={x} />
          ))}
        </div>
      ))}
      {unused.length > 0 && (
        <details className="research-unused">
          <summary>还查到 {unused.length} 篇，报告里没用上</summary>
          {unused.map((x) => (
            <SourceRow key={x.n} x={x} />
          ))}
        </details>
      )}
    </section>
  )
}

const STEPS: { key: string; label: string; phases: ResearchSession['phase'][] }[] = [
  { key: 'plan', label: '拆成几个小问题', phases: ['planning'] },
  { key: 'search', label: '分头查资料', phases: ['searching'] },
  { key: 'reflect', label: '看看还缺什么', phases: ['reflecting'] },
  { key: 'write', label: '写报告', phases: ['writing'] },
]

/** 过程：进行中展开、一步一行；做完收成一行「拆成 4 路 · 补查 1 路 · 用了 9 篇 · 52 秒」，点开再看。 */
function Progress({ s }: { s: ResearchSession }) {
  const [open, setOpen] = useState(false)
  const live = running(s)
  const started = Date.parse(s.startedAt ?? s.createdAt)
  const elapsed = useElapsed(live ? started : null)
  const at = STEPS.findIndex((x) => x.phases.includes(s.phase))
  // 补查时 phase 又回到 searching：已经过了「看看还缺什么」
  const step = s.phase === 'searching' && s.queries.some((x) => x.round === 2) ? 2.5 : at
  const first = s.queries.filter((x) => x.round === 1)
  const extra = s.queries.filter((x) => x.round === 2)
  const secs = s.finishedAt ? Math.max(1, Math.round((Date.parse(s.finishedAt) - started) / 1000)) : null
  const summary = [first.length ? `拆成 ${first.length} 路` : '', extra.length ? `补查 ${extra.length} 路` : '', s.sources.length ? `用了 ${s.sources.length} 篇资料` : '', secs ? `${secs} 秒` : ''].filter(Boolean).join(' · ') || '过程'
  const shown = live || open
  const state = s.phase === 'planning' || s.phase === 'reflecting' ? 'solving' : s.phase === 'searching' ? 'searching' : 'composing'
  const done = (i: number) => !live || i < step

  return (
    <div className="trace research-trace" data-live={live}>
      <button type="button" className="trace-h" aria-expanded={shown} onClick={() => setOpen((v) => !v)} disabled={live}>
        {live && <Orb state={state} className="trace-icon" />}
        {live ? (
          <>
            <span className="shimmer" role="status">
              {s.phase === 'searching' && extra.length ? '补查资料' : STEPS[Math.max(0, at)].label}…
            </span>
            <span className="trace-t" aria-hidden="true">
              {elapsed.toFixed(0)}s
            </span>
          </>
        ) : (
          <span>{summary}</span>
        )}
        {!live && <ChevronDown className="chev" aria-hidden="true" />}
      </button>
      <div className="trace-wrap" data-open={shown}>
        <div className="trace-in">
          <ol className="trace-steps">
            {s.scope.label && (
              <li data-done="true">
                <Check className="ok" aria-hidden="true" />
                <span>{s.scope.label}</span>
              </li>
            )}
            {STEPS.map((x, i) => {
              if (live && i > step) return null
              if (x.key === 'reflect' && !live && !s.gaps.length && !extra.length) return null
              return (
                <li key={x.key} data-done={done(i)}>
                  {done(i) ? <Check className="ok" aria-hidden="true" /> : <i className="ring" aria-hidden="true" />}
                  <span>
                    {x.key === 'search' && extra.length && !live ? '分头查资料、补查' : x.label}
                    {x.key === 'plan' && s.aspects.length > 0 && <small>要讲到：{s.aspects.join('、')}</small>}
                    {x.key === 'search' && first.length > 0 && (
                      <span className="research-queries">
                        {s.queries.map((qq, k) => (
                          <span key={k} data-round={qq.round}>
                            {qq.round === 2 && <b>补查</b>}
                            {qq.q}
                            <small>{qq.found ? `${qq.found} 段` : !live || step > (qq.round === 1 ? 1 : 2.5) ? '没找到' : ''}</small>
                          </span>
                        ))}
                      </span>
                    )}
                    {x.key === 'reflect' && s.gaps.length > 0 && <small>还缺：{s.gaps.join('、')}</small>}
                    {x.key === 'reflect' && done(i) && !s.gaps.length && !extra.length && <small>资料够了</small>}
                    {x.key === 'write' && s.sources.length > 0 && <small>用 {s.sources.length} 篇资料</small>}
                  </span>
                </li>
              )
            })}
          </ol>
        </div>
      </div>
    </div>
  )
}
